import { useScroll, useTransform, MotionValue, useSpring } from 'motion/react';
import { RefObject, useEffect, useState } from 'react';

/**
 * Hook to track scroll progress within a container element
 * Returns a value from 0 to 1 representing scroll progress
 */
export function useScrollProgress(containerRef: RefObject<HTMLElement>): MotionValue<number> {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return scrollYProgress;
}

/**
 * Hook to track scroll progress with spring physics for smoother animations
 */
export function useSpringScrollProgress(
  containerRef: RefObject<HTMLElement>,
  springConfig = { stiffness: 100, damping: 30, mass: 1 }
): MotionValue<number> {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return useSpring(scrollYProgress, springConfig);
}

/**
 * Hook to determine which section/step is currently active based on scroll position
 * Triggers when content reaches the center of the viewport for better UX
 */
export function useActiveStep(
  containerRef: RefObject<HTMLElement>,
  stepCount: number
): number {
  const [activeStep, setActiveStep] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    // Start tracking when top of container reaches center of viewport
    // End tracking when bottom of container reaches center of viewport
    offset: ['start center', 'end center'],
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (value) => {
      // Map progress to steps, starting animations earlier
      const step = Math.min(
        Math.floor(value * (stepCount + 0.5)),
        stepCount - 1
      );
      setActiveStep(Math.max(0, step));
    });

    return () => unsubscribe();
  }, [scrollYProgress, stepCount]);

  return activeStep;
}

/**
 * Hook to track scroll direction
 */
export function useScrollDirection(): 'up' | 'down' | null {
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const updateDirection = () => {
      const scrollY = window.scrollY;
      if (scrollY > lastScrollY) {
        setDirection('down');
      } else if (scrollY < lastScrollY) {
        setDirection('up');
      }
      setLastScrollY(scrollY);
    };

    window.addEventListener('scroll', updateDirection, { passive: true });
    return () => window.removeEventListener('scroll', updateDirection);
  }, [lastScrollY]);

  return direction;
}

/**
 * Hook to transform scroll progress to another range
 * Useful for creating scroll-linked animations
 */
export function useScrollTransform<T>(
  containerRef: RefObject<HTMLElement>,
  inputRange: number[],
  outputRange: T[]
): MotionValue<T> {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  return useTransform(scrollYProgress, inputRange, outputRange);
}

/**
 * Hook to check if reduced motion is preferred
 */
export function useReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return reducedMotion;
}

/**
 * Hook to create stepped scroll progress (snaps to discrete values)
 */
export function useSteppedScrollProgress(
  containerRef: RefObject<HTMLElement>,
  steps: number
): number {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const [steppedProgress, setSteppedProgress] = useState(0);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (value) => {
      const stepped = Math.round(value * steps) / steps;
      setSteppedProgress(stepped);
    });

    return () => unsubscribe();
  }, [scrollYProgress, steps]);

  return steppedProgress;
}
